import type { RefObject } from "react";
import { ArrowRight } from "lucide-react";
import type { AvatarHandle } from "@/features/companion";
import { cn } from "@/lib/utils";
import { MarcusOnboardingIntroAvatar } from "./MarcusOnboardingIntroAvatar";

export const COMPACT_OVERLAY_POSITION =
  "left-1/2 top-[min(calc(100dvh_-_13rem),calc(50dvh_+_10rem))] -translate-x-1/2";

export interface MarcusOnboardingNextStepStatus {
  label: string;
  completedSteps: number;
  remainingSteps: number;
}

export interface MarcusOnboardingCompactGuideProps {
  avatarRef: RefObject<AvatarHandle | null>;
  voiceIntroActive: boolean;
  liveAvatarConnected: boolean;
  liveAvatarSpeaking: boolean;
  highlightLabel?: string | null;
  showNextButton?: boolean;
  nextStep?: MarcusOnboardingNextStepStatus | null;
  countdown?: number;
  onNext?: () => void;
  onDismiss: () => void;
  onAvatarConnected: () => void;
  onAvatarDisconnected: () => void;
  onAvatarStartSpeaking: () => void;
  onAvatarStopSpeaking: () => void;
}

export function MarcusOnboardingCompactGuide({
  avatarRef,
  voiceIntroActive,
  liveAvatarConnected,
  liveAvatarSpeaking,
  highlightLabel,
  showNextButton = false,
  nextStep,
  countdown = 0,
  onNext,
  onDismiss,
  onAvatarConnected,
  onAvatarDisconnected,
  onAvatarStartSpeaking,
  onAvatarStopSpeaking,
}: MarcusOnboardingCompactGuideProps) {
  return (
    <section
      className={cn(
        "relative pointer-events-auto fixed flex w-[280px] max-w-[calc(100vw-2rem)] flex-col items-center gap-4 overflow-hidden rounded-[24px] border border-outline-variant/60 bg-surface-lowest/95 p-5 text-foreground shadow-[0_24px_80px_rgba(28,27,27,0.16)] backdrop-blur-[24px] transition-all duration-500",
        COMPACT_OVERLAY_POSITION,
      )}
    >
      <div className="flex flex-col items-center gap-3">
        <MarcusOnboardingIntroAvatar
          avatarRef={avatarRef}
          compact
          liveConnected={liveAvatarConnected}
          liveSpeaking={liveAvatarSpeaking}
          voiceIntroActive={voiceIntroActive}
          onConnected={onAvatarConnected}
          onDisconnected={onAvatarDisconnected}
          onStartSpeaking={onAvatarStartSpeaking}
          onStopSpeaking={onAvatarStopSpeaking}
        />
        {!showNextButton && voiceIntroActive && highlightLabel && (
          <div
            aria-live="polite"
            className="max-w-full text-center text-sm font-semibold leading-5 text-foreground"
          >
            {highlightLabel}
          </div>
        )}
      </div>

      {showNextButton && (
        <div className="flex w-full flex-col items-center gap-4 pt-2">
          {nextStep ? (
            <div className="flex w-full flex-col items-center gap-2 px-1 text-center">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                Up Next
              </span>
              <span className="text-sm font-medium leading-snug text-foreground">
                {nextStep.label}
              </span>
              <div className="flex flex-wrap items-center justify-center gap-1.5">
                <div
                  className="flex items-center gap-1.5"
                  aria-label={`${nextStep.completedSteps} of ${nextStep.completedSteps + nextStep.remainingSteps} steps done`}
                >
                  {Array.from({
                    length: nextStep.completedSteps + nextStep.remainingSteps,
                  }).map((_, i) => (
                    <div
                      key={i}
                      className={cn(
                        "h-1 rounded-full transition-all duration-300",
                        i < nextStep.completedSteps ? "w-4 bg-primary" : "w-2 bg-primary/20",
                      )}
                    />
                  ))}
                </div>
                <span className="inline-flex items-center rounded-full bg-surface-container px-2.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                  {countdown > 0 ? `Starts in ${countdown}s` : "Ready"}
                </span>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center">
              <span className="inline-flex items-center rounded-full bg-surface-container px-2.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                {countdown > 0 ? `Starts in ${countdown}s` : "Ready"}
              </span>
            </div>
          )}

          <button
            type="button"
            data-highlight-id="marcus-onboarding-next-step"
            aria-label="Continue Marcus onboarding to the next step"
            onClick={onNext}
            className={cn(
              "group relative inline-flex w-full items-center justify-center gap-2 rounded-full",
              "bg-primary px-6 py-3 text-sm font-medium text-primary-foreground shadow-sm",
              "transition-all duration-300 hover:bg-primary-container hover:text-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
            )}
          >
            <span>{countdown > 0 ? "Start now" : "Continue"}</span>
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        </div>
      )}

      <button
        type="button"
        aria-label="Skip Marcus onboarding tour"
        onClick={onDismiss}
        className="inline-flex items-center justify-center rounded-full px-3 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-surface-container hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        Skip tour
      </button>
    </section>
  );
}
